import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import './Home.css';

const Home = () => {
  return (
    <>
      <Helmet>
        <title>
          Psychological Manipulation | Narcissism, Gaslighting & Coercive Control
        </title>

        <meta
          name="description"
          content="An evidence-informed educational resource exploring psychological manipulation, narcissism, gaslighting, coercive control, trauma bonding, and related interpersonal dynamics."
        />
      </Helmet>

      <main className="homePage">
        {/* Hero */}
        <section className="homeHero">
          <h1>Psychological Manipulation</h1>

          <p className="homeSubheadline">
            Understanding manipulation, coercion, narcissistic traits,
            gaslighting, and the psychological dynamics of power and control.
          </p>

          <Link to="/topics" className="primaryAction">
            Explore Topics
          </Link>
        </section>

        {/* Introduction */}
        <section className="homeIntroduction">
          <div className="introductionLayout">
            <div className="introductionContent">
              <h2>Why This Site Exists</h2>

              <p>
                Psychological manipulation can be subtle, cumulative, and
                difficult to recognize while it is happening. Repeated
                distortion, coercion, intimidation, emotional pressure, or
                control can affect a person&apos;s confidence, perception,
                relationships, and ability to make independent decisions.
              </p>

              <p>
                This site was created by Pamela Terrell, B.A. in Sociology, to
                examine manipulation through both interpersonal and social
                perspectives. Topics include narcissistic traits, gaslighting,
                trauma bonding, emotional coercion, controlling behavior,
                triangulation, hoovering, limerence, and related dynamics.
              </p>

              <p>
                The goal is not to diagnose individuals. It is to explain
                concepts carefully, distinguish clinical terminology from
                popular language, examine what research supports, and explore
                how patterns of manipulation can affect human behavior,
                relationships, autonomy, and social functioning.
              </p>
            </div>

            <figure className="introductionFigure">
              <img
                src="/images/control.jpg"
                alt="Symbolic representation of psychological control using a marionette"
                className="introductionImage"
              />

              <figcaption className="imageCredit">
                Photo by{' '}
                <a
                  href="https://unsplash.com/@agni11?utm_content=creditCopyText&utm_medium=referral&utm_source=unsplash"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Sivani Bandaru
                </a>{' '}
                on{' '}
                <a
                  href="https://unsplash.com/photos/2-men-playing-basketball-in-grayscale-photography-bczrpU9n8f4?utm_content=creditCopyText&utm_medium=referral&utm_source=unsplash"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Unsplash
                </a>
              </figcaption>
            </figure>
          </div>
        </section>

        {/* Definition */}
        <section aria-labelledby="manipulation-definition">
          <div className="manipulationDefinition">
            <strong id="manipulation-definition">Manipulation</strong> refers
            broadly to attempts to influence another person&apos;s thoughts,
            emotions, decisions, or behavior in ways that may involve
            deception, exploitation, pressure, or coercion. Manipulative
            behavior becomes especially concerning when it undermines informed
            choice, personal boundaries, or autonomy.
          </div>
        </section>

        {/* Featured Topics */}
        <section className="featuredTopics">
          <h2>Featured Topics</h2>

          <div className="featuredTopicGrid">
            <Link
              to="/topics/narcissism"
              className="featuredTopicCard"
            >
              Narcissism
            </Link>

            <Link
              to="/topics/gaslighting"
              className="featuredTopicCard"
            >
              Gaslighting
            </Link>

            <Link
              to="/topics/triangulation"
              className="featuredTopicCard"
            >
              Triangulation
            </Link>

            <Link
              to="/topics/trauma-bonding"
              className="featuredTopicCard"
            >
              Trauma Bonding
            </Link>

            <Link
              to="/topics/emotional-coercion"
              className="featuredTopicCard"
            >
              Emotional Coercion
            </Link>

            <Link
              to="/topics/hoovering"
              className="featuredTopicCard"
            >
              Hoovering
            </Link>

            <Link
              to="/topics/limerence"
              className="featuredTopicCard"
            >
              Limerence
            </Link>
          </div>
        </section>

        {/* Educational Scope */}
        <section className="researchNote">
          <strong>Educational scope:</strong> This site discusses psychological
          and sociological concepts for educational purposes. Descriptions of
          behaviors should not be used to diagnose another person, and popular
          relationship terminology may not correspond to formal clinical
          diagnoses.
        </section>

        {/* Quote */}
        <section className="homeQuote">
          <blockquote>
            Understanding how influence becomes coercion is an important step
            toward recognizing when power begins to undermine autonomy.
          </blockquote>
        </section>
      </main>
    </>
  );
};

export default Home;